// src/pages/TaskManagement.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, getDocs, getDoc, doc, addDoc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { useUserRole } from '../hooks/useUserRole';
import Navigation from '../components/Navigation';
import { auth } from '../firebase/config';
import { logDataModification } from '../lib/audit/auditLogger';

interface TaskAttachment {
  id: string;
  url: string;
  fileName: string;
  fileSize: number;
  uploadedBy: string;
  uploadedByName?: string;
  uploadedAt: any;
}

interface Task {
  id: string;
  title: string;
  description?: string;
  clientId: string;
  clientName?: string;
  clientEmail?: string;
  clientBusinessName?: string;
  assignedBy: string;
  assignedByName?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'not_started' | 'in_progress' | 'completed' | 'rejected';
  category?: string;
  dueDate?: any;
  comments?: TaskComment[];
  attachments?: TaskAttachment[];
  sequenceOrder?: number; // Sequential order for task completion
  createdAt?: any;
  updatedAt?: any;
}

interface TaskComment {
  id: string;
  userId: string;
  userName?: string;
  comment: string;
  createdAt: any;
}

interface TaskTemplate {
  id: string;
  title: string;
  description?: string;
  category: string;
  defaultPriority: 'low' | 'medium' | 'high' | 'urgent';
  defaultSequenceOrder?: number; // Default sequence order for tasks created from this template
  createdAt?: any;
}

const TaskManagement: React.FC = () => {
  const { role, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  
  // Create task form state
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    clientId: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    category: '',
    dueDate: '',
    sequenceOrder: '',
    attachments: [] as File[]
  });

  useEffect(() => {
    if (!roleLoading && role !== 'admin' && role !== 'staff') {
      navigate('/client-dashboard');
    }
  }, [roleLoading, role, navigate]);

  const loadTasks = async () => {
    try {
      const tasksRef = collection(db, 'tasks');
      const tasksQuery = query(tasksRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(tasksQuery);
      
      const tasksData: Task[] = [];
      snapshot.forEach((doc) => {
        tasksData.push({
          id: doc.id,
          ...doc.data()
        } as Task);
      });
      
      setTasks(tasksData);
      
      // Update selectedTask if it exists, so the detail panel shows updated data
      if (selectedTask) {
        const updatedTask = tasksData.find(t => t.id === selectedTask.id);
        if (updatedTask) {
          setSelectedTask(updatedTask);
        }
      }
    } catch (error: any) {
      console.error('Error loading tasks:', error);
      throw error;
    }
  };

  const loadClients = async () => {
    try {
      const clientsRef = collection(db, 'clients');
      const snapshot = await getDocs(clientsRef);
      
      const clientsData: any[] = [];
      snapshot.forEach((doc) => {
        clientsData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setClients(clientsData);
    } catch (error: any) {
      console.error('Error loading clients:', error);
      throw error;
    }
  };

  const loadTemplates = async () => {
    try {
      const templatesRef = collection(db, 'taskTemplates');
      const templatesQuery = query(templatesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(templatesQuery);
      
      const templatesData: TaskTemplate[] = [];
      snapshot.forEach((doc) => {
        templatesData.push({
          id: doc.id,
          ...doc.data()
        } as TaskTemplate);
      });
      
      setTemplates(templatesData);
    } catch (error: any) {
      console.error('Error loading templates:', error);
      // Templates collection might not exist yet, that's okay
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadTasks(),
        loadClients(),
        loadTemplates()
      ]);
    } catch (error: any) {
      console.error('Error loading data:', error);
      setError('Failed to load data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (role === 'admin' || role === 'staff') {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // Helper function to upload task attachment files
  const uploadTaskAttachment = async (file: File, userId: string): Promise<TaskAttachment> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('clientId', userId);
    formData.append('type', 'documents');

    const response = await fetch('https://helpkeepmymoney.com/upload.php', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload file: ${file.name}`);
    }

    const result = await response.json();
    
    // Get user name for uploadedBy
    let userName = 'Unknown';
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        userName = (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : userData.displayName) || userName;
      }
    } catch (err) {
      // Fallback to displayName or email
      const currentUser = auth.currentUser;
      if (currentUser) {
        userName = currentUser.displayName || currentUser.email || 'Unknown';
      }
    }

    return {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      url: result.fileUrl,
      fileName: result.originalName || file.name,
      fileSize: result.size || file.size,
      uploadedBy: userId,
      uploadedByName: userName,
      uploadedAt: new Date()
    };
  };

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      
      const user = auth.currentUser;
      if (!user) {
        setError('You must be signed in to create tasks');
        return;
      }

      // Get client info
      const client = clients.find(c => c.id === newTask.clientId);
      if (!client) {
        setError('Please select a client');
        return;
      }

      // Upload attachments if any
      let attachments: TaskAttachment[] = [];
      if (newTask.attachments && newTask.attachments.length > 0) {
        try {
          setSuccess(`Uploading ${newTask.attachments.length} file(s)...`);
          setError(''); // Clear any previous errors
          for (let i = 0; i < newTask.attachments.length; i++) {
            const file = newTask.attachments[i];
            setSuccess(`Uploading file ${i + 1} of ${newTask.attachments.length}: ${file.name}...`);
            const attachment = await uploadTaskAttachment(file, user.uid);
            attachments.push(attachment);
          }
          setSuccess(`Successfully uploaded ${attachments.length} file(s). Creating task...`);
        } catch (uploadError: any) {
          setError(`Failed to upload attachments: ${uploadError.message}`);
          setSuccess('');
          return;
        }
      }

      const taskData: any = {
        title: newTask.title,
        description: newTask.description,
        clientId: client.userId || newTask.clientId,
        clientName: client.fullName || (client.firstName && client.lastName ? `${client.firstName} ${client.lastName}` : 'Unknown'),
        clientEmail: client.email,
        clientBusinessName: client.businessName || '',
        assignedBy: user.uid,
        assignedByName: user.displayName || user.email,
        priority: newTask.priority,
        status: 'not_started' as const,
        category: newTask.category || 'General',
        dueDate: newTask.dueDate ? new Date(newTask.dueDate) : null,
        comments: [],
        attachments: attachments.length > 0 ? attachments : [],
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Add sequence order if provided
      if (newTask.sequenceOrder && newTask.sequenceOrder.trim() !== '') {
        const order = parseInt(newTask.sequenceOrder, 10);
        if (!isNaN(order) && order > 0) {
          taskData.sequenceOrder = order;
        }
      }

      const taskDocRef = await addDoc(collection(db, 'tasks'), taskData);

      // Log task creation
      if (user && user.email && role) {
        logDataModification(
          user.uid,
          user.email,
          role as 'admin' | 'staff',
          'task_created',
          'task',
          taskDocRef.id,
          `Task created: ${newTask.title} for client ${client.fullName || client.email}`,
          { clientId: client.userId || newTask.clientId, priority: newTask.priority }
        ).catch((err: any) => console.error('Failed to log task creation:', err));
      }

      setSuccess('Task created successfully!');
      setShowCreateTask(false);
      setNewTask({
        title: '',
        description: '',
        clientId: '',
        priority: 'medium',
        category: '',
        dueDate: '',
        sequenceOrder: '',
        attachments: []
      });
      await loadTasks();
    } catch (error: any) {
      console.error('Error creating task:', error);
      setError('Failed to create task: ' + error.message);
    }
  };

  const handleCreateFromTemplate = async (template: TaskTemplate, clientId: string, dueDate?: string) => {
    try {
      setError('');
      setSuccess('');
      
      const { user } = await import('../firebase/config').then(m => ({ user: m.auth.currentUser }));
      if (!user) {
        setError('You must be signed in to create tasks');
        return;
      }

      const client = clients.find(c => c.id === clientId);
      if (!client) {
        setError('Client not found');
        return;
      }

      const taskData: any = {
        title: template.title,
        description: template.description || '',
        clientId: client.userId || clientId,
        clientName: client.fullName || (client.firstName && client.lastName ? `${client.firstName} ${client.lastName}` : 'Unknown'),
        clientEmail: client.email,
        clientBusinessName: client.businessName || '',
        assignedBy: user.uid,
        assignedByName: user.displayName || user.email,
        priority: template.defaultPriority,
        status: 'not_started' as const,
        category: template.category,
        comments: [],
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Add due date if provided
      if (dueDate) {
        taskData.dueDate = new Date(dueDate);
      }

      // Add default sequence order from template if it exists
      if (template.defaultSequenceOrder !== undefined && template.defaultSequenceOrder !== null) {
        taskData.sequenceOrder = template.defaultSequenceOrder;
      }

      await addDoc(collection(db, 'tasks'), taskData);

      setSuccess('Task created from template!');
      await loadTasks();
    } catch (error: any) {
      console.error('Error creating task from template:', error);
      setError('Failed to create task: ' + error.message);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      const taskRef = doc(db, 'tasks', taskId);
      await deleteDoc(taskRef);

      setSuccess('Task deleted successfully!');
      await loadTasks();
      setSelectedTask(null);
    } catch (error: any) {
      console.error('Error deleting task:', error);
      setError('Failed to delete task: ' + error.message);
    }
  };

  const filteredTasks = tasks.filter((task) => {
    const matchesSearch = 
      task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (task.clientName && task.clientName.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesPriority;
  }).sort((a, b) => {
    // Sort by sequenceOrder if both have it, then by creation date
    if (a.sequenceOrder !== undefined && b.sequenceOrder !== undefined) {
      return a.sequenceOrder - b.sequenceOrder;
    }
    if (a.sequenceOrder !== undefined) return -1;
    if (b.sequenceOrder !== undefined) return 1;
    // If no sequence order, sort by creation date (newest first)
    const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
    const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
    return bDate.getTime() - aDate.getTime();
  });

  const stats = {
    total: tasks.length,
    notStarted: tasks.filter(t => t.status === 'not_started').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    rejected: tasks.filter(t => t.status === 'rejected').length
  };

  if (roleLoading || loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (role !== 'admin' && role !== 'staff') {
    return null;
  }

  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '1600px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h1>Task Management</h1>
          <div style={{ display: 'flex', gap: '1rem' }}>
            {role === 'admin' && (
              <button
                onClick={() => setShowTemplates(!showTemplates)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {showTemplates ? 'Hide Templates' : 'Manage Templates'}
              </button>
            )}
            <button
              onClick={() => setShowCreateTask(!showCreateTask)}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {showCreateTask ? 'Cancel' : '+ Create Task'}
            </button>
            <button
              onClick={() => navigate(role === 'admin' ? '/admin-dashboard' : '/staff-dashboard')}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <StatCard title="Total Tasks" value={stats.total} color="#007bff" />
          <StatCard title="Not Started" value={stats.notStarted} color="#6c757d" />
          <StatCard title="In Progress" value={stats.inProgress} color="#ffc107" />
          <StatCard title="Completed" value={stats.completed} color="#28a745" />
          <StatCard title="Rejected" value={stats.rejected} color="#dc3545" />
        </div>

        {/* Alerts */}
        {error && (
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid #f5c6cb'
          }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{
            backgroundColor: '#d4edda',
            color: '#155724',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid #c3e6cb'
          }}>
            {success}
          </div>
        )}

        {/* Task Templates Section (Admin Only) */}
        {showTemplates && role === 'admin' && (
          <TaskTemplatesSection 
            templates={templates}
            onTemplatesChange={loadTemplates}
            clients={clients}
            onCreateFromTemplate={handleCreateFromTemplate}
          />
        )}

        {/* Create Task Form */}
        {showCreateTask && (
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '2rem',
            borderRadius: '8px',
            marginBottom: '2rem'
          }}>
            <h2 style={{ marginTop: 0 }}>Create New Task</h2>
            <form onSubmit={handleCreateTask}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Task Title *
                </label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Description
                </label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    Assign to Client *
                  </label>
                  <select
                    value={newTask.clientId}
                    onChange={(e) => setNewTask({ ...newTask, clientId: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="">Select a client...</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.fullName || (client.firstName && client.lastName ? `${client.firstName} ${client.lastName}` : client.email)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    Priority *
                  </label>
                  <select
                    value={newTask.priority}
                    onChange={(e) => setNewTask({ ...newTask, priority: e.target.value as any })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    Category
                  </label>
                  <input
                    type="text"
                    value={newTask.category}
                    onChange={(e) => setNewTask({ ...newTask, category: e.target.value })}
                    placeholder="e.g., Bookkeeping, Payroll, etc."
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={newTask.dueDate}
                    onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Sequence Order (Optional)
                </label>
                <input
                  type="number"
                  min="1"
                  value={newTask.sequenceOrder}
                  onChange={(e) => setNewTask({ ...newTask, sequenceOrder: e.target.value })}
                  placeholder="Order (1, 2, 3...)"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Attachments (Optional)
                </label>
                <input
                  type="file"
                  multiple
                  onChange={(e) => {
                    if (e.target.files) {
                      setNewTask({ ...newTask, attachments: Array.from(e.target.files) });
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                />
                {newTask.attachments && newTask.attachments.length > 0 && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6c757d' }}>
                    {newTask.attachments.length} file(s) selected
                  </div>
                )}
                <small style={{ display: 'block', marginTop: '0.25rem', color: '#6c757d', fontSize: '0.875rem' }}>
                  Max file size: 10MB. Supported: PDF, Word, Excel, Images, Text files
                </small>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  type="submit"
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  Create Task
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateTask(false);
                    setNewTask({
                      title: '',
                      description: '',
                      clientId: '',
                      priority: 'medium',
                      category: '',
                      dueDate: '',
                      sequenceOrder: '',
                      attachments: []
                    });
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Filters */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '1.5rem',
          borderRadius: '8px',
          marginBottom: '2rem',
          display: 'flex',
          gap: '1rem',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <div style={{ flex: '1', minWidth: '300px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Search Tasks
            </label>
            <input
              type="text"
              placeholder="Search by title, description, or client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '1rem'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Filter by Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '0.5rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Statuses</option>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Filter by Priority
            </label>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              style={{
                padding: '0.5rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>

        {/* Tasks List and Details */}
        <div style={{ display: 'grid', gridTemplateColumns: selectedTask ? '1fr 1fr' : '1fr', gap: '2rem' }}>
          {/* Tasks Table */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Task</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Client</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Priority</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Status</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Due Date</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                      {loading ? 'Loading tasks...' : 'No tasks found'}
                    </td>
                  </tr>
                ) : (
                  filteredTasks.map((task) => (
                    <tr 
                      key={task.id} 
                      onClick={() => setSelectedTask(task)}
                      style={{ 
                        borderBottom: '1px solid #dee2e6',
                        backgroundColor: selectedTask?.id === task.id ? '#e7f3ff' : 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <td style={{ padding: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          {task.sequenceOrder !== undefined && task.sequenceOrder !== null && (
                            <div style={{
                              minWidth: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              backgroundColor: '#007bff',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              fontSize: '0.875rem',
                              flexShrink: 0
                            }}>
                              {task.sequenceOrder}
                            </div>
                          )}
                          <div>
                            <div style={{ fontWeight: '500' }}>{task.title}</div>
                            {task.category && (
                              <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>{task.category}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div>
                          <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>{task.clientName || 'Unknown'}</div>
                          {(() => {
                            // Try to get business name from task or lookup from clients
                            const businessName = task.clientBusinessName || 
                              (clients.find(c => c.userId === task.clientId || c.email === task.clientEmail)?.businessName);
                            return businessName ? (
                              <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{businessName}</div>
                            ) : null;
                          })()}
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          backgroundColor: 
                            task.priority === 'urgent' ? '#dc3545' :
                            task.priority === 'high' ? '#fd7e14' :
                            task.priority === 'medium' ? '#ffc107' : '#28a745',
                          color: task.priority === 'medium' ? '#000' : '#fff'
                        }}>
                          {task.priority}
                        </span>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          backgroundColor: 
                            task.status === 'completed' ? '#28a745' :
                            task.status === 'in_progress' ? '#ffc107' :
                            task.status === 'rejected' ? '#dc3545' : '#6c757d',
                          color: task.status === 'in_progress' ? '#000' : '#fff'
                        }}>
                          {task.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {task.dueDate?.toDate ? task.dueDate.toDate().toLocaleDateString() : 'N/A'}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTask(task.id);
                          }}
                          style={{
                            padding: '0.25rem 0.75rem',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Task Details Panel */}
          {selectedTask && (
            <TaskDetailPanel 
              task={selectedTask}
              onClose={() => setSelectedTask(null)}
              onUpdate={loadTasks}
              role={role}
            />
          )}
        </div>
      </div>
    </div>
  );
};

interface StatCardProps {
  title: string;
  value: number;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, color }) => (
  <div style={{
    backgroundColor: 'white',
    padding: '1rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    borderLeft: `4px solid ${color}`
  }}>
    <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.5rem' }}>
      {title}
    </div>
    <div style={{ fontSize: '2rem', fontWeight: 'bold', color }}>
      {value}
    </div>
  </div>
);

interface TaskTemplatesSectionProps {
  templates: TaskTemplate[];
  onTemplatesChange: () => void;
  clients: any[];
  onCreateFromTemplate: (template: TaskTemplate, clientId: string, dueDate?: string) => void;
}

const TaskTemplatesSection: React.FC<TaskTemplatesSectionProps> = ({ 
  templates, 
  onTemplatesChange,
  clients,
  onCreateFromTemplate
}) => {
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [newTemplate, setNewTemplate] = useState({
    title: '',
    description: '',
    category: '',
    defaultPriority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    defaultSequenceOrder: ''
  });
  const [editTemplate, setEditTemplate] = useState({
    title: '',
    description: '',
    category: '',
    defaultPriority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    defaultSequenceOrder: ''
  });
  const [selectedClientForTemplate, setSelectedClientForTemplate] = useState<string>('');
  const [templateDueDate, setTemplateDueDate] = useState<string>('');
  const [selectedTemplates, setSelectedTemplates] = useState<Set<string>>(new Set());
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [bulkClientId, setBulkClientId] = useState<string>('');
  const [bulkTemplateDueDates, setBulkTemplateDueDates] = useState<Record<string, string>>({});

  const handleTemplateToggle = (templateId: string) => {
    setSelectedTemplates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  const handleBulkAssign = async () => {
    if (!bulkClientId || selectedTemplates.size === 0) {
      alert('Please select at least one template and a client');
      return;
    }

    try {
      const selectedTemplateObjects = templates.filter(t => selectedTemplates.has(t.id));
      for (const template of selectedTemplateObjects) {
        const dueDate = bulkTemplateDueDates[template.id] || undefined;
        await onCreateFromTemplate(template, bulkClientId, dueDate);
      }
      
      setSelectedTemplates(new Set());
      setShowBulkAssign(false);
      setBulkClientId('');
      setBulkTemplateDueDates({});
      alert(`Successfully assigned ${selectedTemplateObjects.length} template(s) to client`);
    } catch (error: any) {
      alert(`Failed to assign templates: ${error.message}`);
    }
  };

  const handleCreateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const templateData: any = {
        title: newTemplate.title,
        description: newTemplate.description,
        category: newTemplate.category,
        defaultPriority: newTemplate.defaultPriority,
        createdAt: new Date()
      };

      // Add default sequence order if provided
      if (newTemplate.defaultSequenceOrder && newTemplate.defaultSequenceOrder.trim() !== '') {
        const order = parseInt(newTemplate.defaultSequenceOrder, 10);
        if (!isNaN(order) && order > 0) {
          templateData.defaultSequenceOrder = order;
        }
      }

      await addDoc(collection(db, 'taskTemplates'), templateData);

      setShowCreateTemplate(false);
      setNewTemplate({
        title: '',
        description: '',
        category: '',
        defaultPriority: 'medium',
        defaultSequenceOrder: ''
      });
      onTemplatesChange();
    } catch (error: any) {
      console.error('Error creating template:', error);
      alert('Failed to create template: ' + error.message);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'taskTemplates', templateId));
      onTemplatesChange();
    } catch (error: any) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template: ' + error.message);
    }
  };

  const handleStartEdit = (template: TaskTemplate) => {
    setEditingTemplateId(template.id);
    setEditTemplate({
      title: template.title,
      description: template.description || '',
      category: template.category || '',
      defaultPriority: template.defaultPriority,
      defaultSequenceOrder: template.defaultSequenceOrder !== undefined && template.defaultSequenceOrder !== null ? template.defaultSequenceOrder.toString() : ''
    });
  };

  const handleCancelEdit = () => {
    setEditingTemplateId(null);
    setEditTemplate({
      title: '',
      description: '',
      category: '',
      defaultPriority: 'medium',
      defaultSequenceOrder: ''
    });
  };

  const handleSaveEdit = async (templateId: string) => {
    try {
      const templateRef = doc(db, 'taskTemplates', templateId);
      const updateData: any = {
        title: editTemplate.title,
        description: editTemplate.description,
        category: editTemplate.category,
        defaultPriority: editTemplate.defaultPriority,
        updatedAt: new Date()
      };

      // Add default sequence order if provided
      if (editTemplate.defaultSequenceOrder && editTemplate.defaultSequenceOrder.trim() !== '') {
        const order = parseInt(editTemplate.defaultSequenceOrder, 10);
        if (!isNaN(order) && order > 0) {
          updateData.defaultSequenceOrder = order;
        }
      } else {
        // If empty, remove default sequence order
        updateData.defaultSequenceOrder = null;
      }

      await updateDoc(templateRef, updateData);

      setEditingTemplateId(null);
      onTemplatesChange();
    } catch (error: any) {
      console.error('Error updating template:', error);
      alert('Failed to update template: ' + error.message);
    }
  };

  const categories = Array.from(new Set(templates.map(t => t.category).filter(Boolean))).sort();

  return (
    <div style={{
      backgroundColor: '#f8f9fa',
      padding: '2rem',
      borderRadius: '8px',
      marginBottom: '2rem'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0 }}>Standard Task Templates</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {selectedTemplates.size > 0 && (
            <button
              onClick={() => setShowBulkAssign(true)}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Bulk Assign ({selectedTemplates.size})
            </button>
          )}
          <button
            onClick={() => setShowCreateTemplate(!showCreateTemplate)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: showCreateTemplate ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {showCreateTemplate ? 'Cancel' : '+ Create Template'}
          </button>
        </div>
      </div>

      {/* Bulk Assignment Modal */}
      {showBulkAssign && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '1.5rem' }}>Bulk Assign Templates</h2>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Select Client *
              </label>
              <select
                value={bulkClientId}
                onChange={(e) => setBulkClientId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
              >
                <option value="">Select a client...</option>
                {clients.map(client => {
                  const clientName = client.fullName || (client.firstName && client.lastName ? `${client.firstName} ${client.lastName}` : client.email);
                  const businessName = client.businessName || '';
                  const displayText = businessName ? `${clientName} - ${businessName}` : clientName;
                  return (
                    <option key={client.id} value={client.id}>
                      {displayText}
                    </option>
                  );
                })}
              </select>
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 'bold' }}>
                Selected Templates ({selectedTemplates.size})
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '300px', overflowY: 'auto', padding: '0.5rem' }}>
                {templates
                  .filter(t => selectedTemplates.has(t.id))
                  .sort((a, b) => {
                    // Sort by defaultSequenceOrder if both have it
                    if (a.defaultSequenceOrder !== undefined && b.defaultSequenceOrder !== undefined) {
                      return a.defaultSequenceOrder - b.defaultSequenceOrder;
                    }
                    // Templates with sequence order come first
                    if (a.defaultSequenceOrder !== undefined) return -1;
                    if (b.defaultSequenceOrder !== undefined) return 1;
                    // If no sequence order, sort alphabetically by title
                    return a.title.localeCompare(b.title);
                  })
                  .map(template => (
                    <div key={template.id} style={{
                      padding: '1rem',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      backgroundColor: '#f8f9fa'
                    }}>
                      <div style={{ marginBottom: '0.5rem', fontWeight: '500' }}>
                        {template.title}
                        {template.category && (
                          <span style={{ fontSize: '0.875rem', color: '#6c757d', marginLeft: '0.5rem' }}>
                            ({template.category})
                          </span>
                        )}
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: '500' }}>
                          Due Date (Optional)
                        </label>
                        <input
                          type="date"
                          value={bulkTemplateDueDates[template.id] || ''}
                          onChange={(e) => {
                            setBulkTemplateDueDates(prev => ({
                              ...prev,
                              [template.id]: e.target.value
                            }));
                          }}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            fontSize: '0.875rem'
                          }}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowBulkAssign(false);
                  setBulkClientId('');
                  setBulkTemplateDueDates({});
                }}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAssign}
                disabled={!bulkClientId}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: bulkClientId ? '#28a745' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: bulkClientId ? 'pointer' : 'not-allowed'
                }}
              >
                Assign {selectedTemplates.size} Template(s)
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateTemplate && (
        <form onSubmit={handleCreateTemplate} style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: 'white', borderRadius: '8px' }}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Template Title *</label>
            <input
              type="text"
              value={newTemplate.title}
              onChange={(e) => setNewTemplate({ ...newTemplate, title: e.target.value })}
              required
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px' }}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Description</label>
            <textarea
              value={newTemplate.description}
              onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
              rows={3}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px' }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Category *</label>
              <input
                type="text"
                value={newTemplate.category}
                onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value })}
                required
                placeholder="e.g., Bookkeeping, Payroll"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Default Priority *</label>
              <select
                value={newTemplate.defaultPriority}
                onChange={(e) => setNewTemplate({ ...newTemplate, defaultPriority: e.target.value as any })}
                required
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px' }}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Default Sequence Order</label>
            <input
              type="number"
              min="1"
              value={newTemplate.defaultSequenceOrder}
              onChange={(e) => setNewTemplate({ ...newTemplate, defaultSequenceOrder: e.target.value })}
              placeholder="Order (1, 2, 3...) - Optional"
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px' }}
            />
            <small style={{ color: '#6c757d', fontSize: '0.75rem' }}>Tasks created from this template will use this sequence order by default</small>
          </div>
          <button
            type="submit"
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Create Template
          </button>
        </form>
      )}

      {/* Group templates by category */}
      {categories.length > 0 ? (
        categories.map(category => {
          const categoryTemplates = templates
            .filter(t => t.category === category)
            .sort((a, b) => {
              // Sort by defaultSequenceOrder if both have it
              if (a.defaultSequenceOrder !== undefined && b.defaultSequenceOrder !== undefined) {
                return a.defaultSequenceOrder - b.defaultSequenceOrder;
              }
              // Templates with sequence order come first
              if (a.defaultSequenceOrder !== undefined) return -1;
              if (b.defaultSequenceOrder !== undefined) return 1;
              // If no sequence order, sort alphabetically by title
              return a.title.localeCompare(b.title);
            });
          return (
            <div key={category} style={{ marginBottom: '2rem' }}>
              <h3 style={{ marginBottom: '1rem', color: '#495057' }}>{category}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
                {categoryTemplates.map(template => (
                  <div key={template.id} style={{
                    backgroundColor: 'white',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: selectedTemplates.has(template.id) ? '2px solid #007bff' : '1px solid #dee2e6',
                    position: 'relative'
                  }}>
                    <div style={{ position: 'absolute', top: '0.5rem', right: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={selectedTemplates.has(template.id)}
                        onChange={() => handleTemplateToggle(template.id)}
                        style={{
                          width: '20px',
                          height: '20px',
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                    {editingTemplateId === template.id ? (
                      <div>
                        <div style={{ marginBottom: '0.75rem' }}>
                          <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: 'bold' }}>
                            Title *
                          </label>
                          <input
                            type="text"
                            value={editTemplate.title}
                            onChange={(e) => setEditTemplate({ ...editTemplate, title: e.target.value })}
                            required
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              fontSize: '0.875rem'
                            }}
                          />
                        </div>
                        <div style={{ marginBottom: '0.75rem' }}>
                          <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: 'bold' }}>
                            Description
                          </label>
                          <textarea
                            value={editTemplate.description}
                            onChange={(e) => setEditTemplate({ ...editTemplate, description: e.target.value })}
                            rows={2}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                              fontFamily: 'inherit'
                            }}
                          />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: 'bold' }}>
                              Category *
                            </label>
                            <input
                              type="text"
                              value={editTemplate.category}
                              onChange={(e) => setEditTemplate({ ...editTemplate, category: e.target.value })}
                              required
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #ced4da',
                                borderRadius: '4px',
                                fontSize: '0.875rem'
                              }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: 'bold' }}>
                              Priority *
                            </label>
                            <select
                              value={editTemplate.defaultPriority}
                              onChange={(e) => setEditTemplate({ ...editTemplate, defaultPriority: e.target.value as any })}
                              required
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #ced4da',
                                borderRadius: '4px',
                                fontSize: '0.875rem'
                              }}
                            >
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High</option>
                              <option value="urgent">Urgent</option>
                            </select>
                          </div>
                        </div>
                        <div style={{ marginBottom: '0.75rem' }}>
                          <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: 'bold' }}>
                            Default Sequence Order
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={editTemplate.defaultSequenceOrder}
                            onChange={(e) => setEditTemplate({ ...editTemplate, defaultSequenceOrder: e.target.value })}
                            placeholder="Order (1, 2, 3...) - Optional"
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              fontSize: '0.875rem'
                            }}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => handleSaveEdit(template.id)}
                            style={{
                              flex: 1,
                              padding: '0.5rem',
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.875rem'
                            }}
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            style={{
                              flex: 1,
                              padding: '0.5rem',
                              backgroundColor: '#6c757d',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.875rem'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            {template.defaultSequenceOrder !== undefined && template.defaultSequenceOrder !== null && (
                              <div style={{
                                minWidth: '28px',
                                height: '28px',
                                borderRadius: '50%',
                                backgroundColor: '#007bff',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                fontSize: '0.75rem',
                                flexShrink: 0
                              }}>
                                {template.defaultSequenceOrder}
                              </div>
                            )}
                            <h4 style={{ margin: 0 }}>{template.title}</h4>
                          </div>
                          <div style={{ display: 'flex', gap: '0.25rem', paddingRight: '32px' }}>
                            <button
                              onClick={() => handleStartEdit(template)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.75rem'
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteTemplate(template.id)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.75rem'
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                        {template.description && (
                          <p style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                            {template.description}
                          </p>
                        )}
                        <div style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                          Priority: <strong>{template.defaultPriority}</strong>
                        </div>
                      </>
                    )}
                    {editingTemplateId !== template.id && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <select
                          value={selectedClientForTemplate}
                          onChange={(e) => setSelectedClientForTemplate(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            fontSize: '0.875rem'
                          }}
                        >
                          <option value="">Select client...</option>
                          {clients.map(client => {
                            const clientName = client.fullName || (client.firstName && client.lastName ? `${client.firstName} ${client.lastName}` : client.email);
                            const businessName = client.businessName || '';
                            const displayText = businessName ? `${clientName} - ${businessName}` : clientName;
                            return (
                              <option key={client.id} value={client.id}>
                                {displayText}
                              </option>
                            );
                          })}
                        </select>
                        {selectedClientForTemplate && (
                          <input
                            type="date"
                            value={templateDueDate}
                            onChange={(e) => setTemplateDueDate(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              fontSize: '0.875rem'
                            }}
                          />
                        )}
                        <button
                          onClick={() => {
                            if (selectedClientForTemplate) {
                              onCreateFromTemplate(template, selectedClientForTemplate, templateDueDate || undefined);
                              setSelectedClientForTemplate('');
                              setTemplateDueDate('');
                            }
                          }}
                          disabled={!selectedClientForTemplate}
                          style={{
                            padding: '0.5rem',
                            backgroundColor: selectedClientForTemplate ? '#007bff' : '#ccc',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: selectedClientForTemplate ? 'pointer' : 'not-allowed',
                            fontSize: '0.875rem'
                          }}
                        >
                          Assign Task
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })
      ) : (
        <p style={{ color: '#6c757d' }}>No templates created yet. Create your first template above.</p>
      )}

      {templates.filter(t => !t.category).length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#495057' }}>Uncategorized</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
            {templates
              .filter(t => !t.category)
              .sort((a, b) => {
                // Sort by defaultSequenceOrder if both have it
                if (a.defaultSequenceOrder !== undefined && b.defaultSequenceOrder !== undefined) {
                  return a.defaultSequenceOrder - b.defaultSequenceOrder;
                }
                // Templates with sequence order come first
                if (a.defaultSequenceOrder !== undefined) return -1;
                if (b.defaultSequenceOrder !== undefined) return 1;
                // If no sequence order, sort alphabetically by title
                return a.title.localeCompare(b.title);
              })
              .map(template => (
              <div key={template.id} style={{
                backgroundColor: 'white',
                padding: '1rem',
                borderRadius: '8px',
                border: selectedTemplates.has(template.id) ? '2px solid #007bff' : '1px solid #dee2e6',
                position: 'relative'
              }}>
                <div style={{ position: 'absolute', top: '0.5rem', right: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={selectedTemplates.has(template.id)}
                    onChange={() => handleTemplateToggle(template.id)}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {template.defaultSequenceOrder !== undefined && template.defaultSequenceOrder !== null && (
                      <div style={{
                        minWidth: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: '#007bff',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        flexShrink: 0
                      }}>
                        {template.defaultSequenceOrder}
                      </div>
                    )}
                    <h4 style={{ margin: 0 }}>{template.title}</h4>
                  </div>
                  <div style={{ paddingRight: '32px' }}>
                    <button
                      onClick={() => handleDeleteTemplate(template.id)}
                      style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                        {template.description && (
                          <p style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                            {template.description}
                          </p>
                        )}
                        <div style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                          Priority: <strong>{template.defaultPriority}</strong>
                        </div>
                    {editingTemplateId === template.id ? (
                      <div>
                        <div style={{ marginBottom: '0.75rem' }}>
                          <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: 'bold' }}>
                            Title *
                          </label>
                          <input
                            type="text"
                            value={editTemplate.title}
                            onChange={(e) => setEditTemplate({ ...editTemplate, title: e.target.value })}
                            required
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              fontSize: '0.875rem'
                            }}
                          />
                        </div>
                        <div style={{ marginBottom: '0.75rem' }}>
                          <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: 'bold' }}>
                            Description
                          </label>
                          <textarea
                            value={editTemplate.description}
                            onChange={(e) => setEditTemplate({ ...editTemplate, description: e.target.value })}
                            rows={2}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                              fontFamily: 'inherit'
                            }}
                          />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: 'bold' }}>
                              Category *
                            </label>
                            <input
                              type="text"
                              value={editTemplate.category}
                              onChange={(e) => setEditTemplate({ ...editTemplate, category: e.target.value })}
                              required
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #ced4da',
                                borderRadius: '4px',
                                fontSize: '0.875rem'
                              }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: 'bold' }}>
                              Priority *
                            </label>
                            <select
                              value={editTemplate.defaultPriority}
                              onChange={(e) => setEditTemplate({ ...editTemplate, defaultPriority: e.target.value as any })}
                              required
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #ced4da',
                                borderRadius: '4px',
                                fontSize: '0.875rem'
                              }}
                            >
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High</option>
                              <option value="urgent">Urgent</option>
                            </select>
                          </div>
                        </div>
                        <div style={{ marginBottom: '0.75rem' }}>
                          <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: 'bold' }}>
                            Default Sequence Order
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={editTemplate.defaultSequenceOrder}
                            onChange={(e) => setEditTemplate({ ...editTemplate, defaultSequenceOrder: e.target.value })}
                            placeholder="Order (1, 2, 3...) - Optional"
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              fontSize: '0.875rem'
                            }}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => handleSaveEdit(template.id)}
                            style={{
                              flex: 1,
                              padding: '0.5rem',
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.875rem'
                            }}
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            style={{
                              flex: 1,
                              padding: '0.5rem',
                              backgroundColor: '#6c757d',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.875rem'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            {template.defaultSequenceOrder !== undefined && template.defaultSequenceOrder !== null && (
                              <div style={{
                                minWidth: '28px',
                                height: '28px',
                                borderRadius: '50%',
                                backgroundColor: '#007bff',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                fontSize: '0.75rem',
                                flexShrink: 0
                              }}>
                                {template.defaultSequenceOrder}
                              </div>
                            )}
                            <h4 style={{ margin: 0 }}>{template.title}</h4>
                          </div>
                          <div style={{ display: 'flex', gap: '0.25rem', paddingRight: '32px' }}>
                            <button
                              onClick={() => handleStartEdit(template)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.75rem'
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteTemplate(template.id)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.75rem'
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                        {template.description && (
                          <p style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                            {template.description}
                          </p>
                        )}
                        <div style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                          Priority: <strong>{template.defaultPriority}</strong>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <select
                            value={selectedClientForTemplate}
                            onChange={(e) => setSelectedClientForTemplate(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              fontSize: '0.875rem'
                            }}
                          >
                            <option value="">Select client...</option>
                            {clients.map(client => {
                              const clientName = client.fullName || (client.firstName && client.lastName ? `${client.firstName} ${client.lastName}` : client.email);
                              const businessName = client.businessName || '';
                              const displayText = businessName ? `${clientName} - ${businessName}` : clientName;
                              return (
                                <option key={client.id} value={client.id}>
                                  {displayText}
                                </option>
                              );
                            })}
                          </select>
                          {selectedClientForTemplate && (
                            <input
                              type="date"
                              value={templateDueDate}
                              onChange={(e) => setTemplateDueDate(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #ced4da',
                                borderRadius: '4px',
                                fontSize: '0.875rem'
                              }}
                            />
                          )}
                          <button
                            onClick={() => {
                              if (selectedClientForTemplate) {
                                onCreateFromTemplate(template, selectedClientForTemplate, templateDueDate || undefined);
                                setSelectedClientForTemplate('');
                                setTemplateDueDate('');
                              }
                            }}
                            disabled={!selectedClientForTemplate}
                            style={{
                              padding: '0.5rem',
                              backgroundColor: selectedClientForTemplate ? '#007bff' : '#ccc',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: selectedClientForTemplate ? 'pointer' : 'not-allowed',
                              fontSize: '0.875rem'
                            }}
                          >
                            Assign Task
                          </button>
                        </div>
                      </>
                    )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface TaskDetailPanelProps {
  task: Task;
  onClose: () => void;
  onUpdate: () => void;
  role: 'admin' | 'staff' | 'client' | null;
}

const TaskDetailPanel: React.FC<TaskDetailPanelProps> = ({ task, onClose, onUpdate, role }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editPriority, setEditPriority] = useState(task.priority);
  const [editCategory, setEditCategory] = useState(task.category || '');
  const [editDueDate, setEditDueDate] = useState(
    task.dueDate?.toDate ? task.dueDate.toDate().toISOString().split('T')[0] : ''
  );
  const [editSequenceOrder, setEditSequenceOrder] = useState(
    task.sequenceOrder !== undefined ? task.sequenceOrder.toString() : ''
  );
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [currentTask, setCurrentTask] = useState<Task>(task);

  // Update currentTask when task prop changes
  useEffect(() => {
    setCurrentTask(task);
  }, [task]);

  const handleUpdateTask = async () => {
    try {
      const taskRef = doc(db, 'tasks', currentTask.id);
      const updateData: any = {
        priority: editPriority,
        category: editCategory,
        dueDate: editDueDate ? new Date(editDueDate) : null,
        updatedAt: new Date()
      };

      // Add sequence order if provided
      if (editSequenceOrder && editSequenceOrder.trim() !== '') {
        const order = parseInt(editSequenceOrder, 10);
        if (!isNaN(order) && order > 0) {
          updateData.sequenceOrder = order;
        }
      } else {
        // If empty, remove sequence order
        updateData.sequenceOrder = null;
      }

      await updateDoc(taskRef, updateData);

      // Log task update
      const user = auth.currentUser;
      if (user && user.email && role) {
        logDataModification(
          user.uid,
          user.email,
          role as 'admin' | 'staff',
          'task_updated',
          'task',
          currentTask.id,
          `Task updated: ${currentTask.title}`,
          { priority: editPriority, category: editCategory }
        ).catch((err: any) => console.error('Failed to log task update:', err));
      }

      setIsEditing(false);
      onUpdate();
    } catch (error: any) {
      console.error('Error updating task:', error);
      alert('Failed to update task: ' + error.message);
    }
  };

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      padding: '2rem',
      position: 'sticky',
      top: '2rem',
      maxHeight: 'calc(100vh - 4rem)',
      overflowY: 'auto'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0 }}>Task Details</h2>
        <button
          onClick={onClose}
          style={{
            padding: '0.5rem',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          ✕
        </button>
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#495057' }}>Task Information</h3>
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Title</div>
          <div style={{ fontSize: '1rem', fontWeight: '500' }}>{task.title}</div>
        </div>
        {task.description && (
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Description</div>
            <div style={{ fontSize: '1rem' }}>{task.description}</div>
          </div>
        )}
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Client</div>
          <div style={{ fontSize: '1rem', fontWeight: '500' }}>{task.clientName || 'Unknown'}</div>
        </div>
        {(role === 'admin' || role === 'staff') && (
          <>
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Priority</div>
              {isEditing ? (
                <select
                  value={editPriority}
                  onChange={(e) => setEditPriority(e.target.value as any)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              ) : (
                <span style={{
                  padding: '0.25rem 0.75rem',
                  borderRadius: '12px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  backgroundColor: 
                    task.priority === 'urgent' ? '#dc3545' :
                    task.priority === 'high' ? '#fd7e14' :
                    task.priority === 'medium' ? '#ffc107' : '#28a745',
                  color: task.priority === 'medium' ? '#000' : '#fff'
                }}>
                  {task.priority}
                </span>
              )}
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Category</div>
              {isEditing ? (
                <input
                  type="text"
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                />
              ) : (
                <div style={{ fontSize: '1rem' }}>{task.category || 'N/A'}</div>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
              <div>
                <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Due Date</div>
                {isEditing ? (
                  <input
                    type="date"
                    value={editDueDate}
                    onChange={(e) => setEditDueDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '1rem'
                    }}
                  />
                ) : (
                  <div style={{ fontSize: '1rem' }}>
                    {task.dueDate?.toDate ? task.dueDate.toDate().toLocaleDateString() : 'No due date'}
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Sequence Order</div>
                {isEditing ? (
                  <input
                    type="number"
                    min="1"
                    value={editSequenceOrder}
                    onChange={(e) => setEditSequenceOrder(e.target.value)}
                    placeholder="Order (1, 2, 3...)"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '1rem'
                    }}
                  />
                ) : (
                  <div style={{ fontSize: '1rem' }}>
                    {task.sequenceOrder !== undefined && task.sequenceOrder !== null ? `#${task.sequenceOrder}` : 'No order'}
                  </div>
                )}
              </div>
            </div>
            {isEditing ? (
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button
                  onClick={handleUpdateTask}
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditPriority(currentTask.priority);
                    setEditCategory(currentTask.category || '');
                    setEditDueDate(currentTask.dueDate?.toDate ? currentTask.dueDate.toDate().toISOString().split('T')[0] : '');
                  }}
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                style={{
                  marginTop: '1rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Edit Task
              </button>
            )}
          </>
        )}
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Status</div>
          <span style={{
            padding: '0.25rem 0.75rem',
            borderRadius: '12px',
            fontSize: '0.875rem',
            fontWeight: '500',
            backgroundColor: 
              task.status === 'completed' ? '#28a745' :
              task.status === 'in_progress' ? '#ffc107' :
              task.status === 'rejected' ? '#dc3545' : '#6c757d',
            color: task.status === 'in_progress' ? '#000' : '#fff'
          }}>
            {task.status.replace('_', ' ')}
          </span>
        </div>
        {task.createdAt && (
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>Created</div>
            <div style={{ fontSize: '1rem' }}>
              {task.createdAt?.toDate ? task.createdAt.toDate().toLocaleString() : 'N/A'}
            </div>
          </div>
        )}
      </div>

      {/* Attachments Section */}
      <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #dee2e6' }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#495057' }}>Attachments</h3>
        {currentTask.attachments && currentTask.attachments.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
            {currentTask.attachments.map((attachment) => (
              <div key={attachment.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.75rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>{attachment.fileName}</div>
                  <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.25rem' }}>
                    {(attachment.fileSize / 1024).toFixed(2)} KB
                    {attachment.uploadedByName && ` • Uploaded by ${attachment.uploadedByName}`}
                    {attachment.uploadedAt && attachment.uploadedAt.toDate && (
                      ` • ${attachment.uploadedAt.toDate().toLocaleDateString()}`
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <a
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#007bff',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}
                  >
                    Download
                  </a>
                  {(role === 'admin' || role === 'staff') && (
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Are you sure you want to delete "${attachment.fileName}"?`)) {
                          return;
                        }
                        try {
                          const taskRef = doc(db, 'tasks', currentTask.id);
                          const currentAttachments = currentTask.attachments || [];
                          const updatedAttachments = currentAttachments.filter(a => a.id !== attachment.id);
                          
                          await updateDoc(taskRef, {
                            attachments: updatedAttachments,
                            updatedAt: new Date()
                          });
                          
                          // Update local state immediately so UI updates without waiting for reload
                          setCurrentTask(prev => ({
                            ...prev,
                            attachments: updatedAttachments,
                            updatedAt: new Date()
                          }));
                          
                          // Also reload tasks to sync with the tasks list
                          onUpdate();
                        } catch (error: any) {
                          alert(`Failed to delete attachment: ${error.message}`);
                        }
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        cursor: 'pointer'
                      }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <div>
          {uploadingAttachment && (
            <div style={{
              marginBottom: '1rem',
              padding: '1rem',
              backgroundColor: '#e7f3ff',
              border: '1px solid #b3d9ff',
              borderRadius: '4px',
              color: '#0066cc'
            }}>
              <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>📤 Uploading files...</div>
              <div style={{ fontSize: '0.875rem' }}>Please wait while your files are being uploaded.</div>
            </div>
          )}
          <input
            type="file"
            multiple
            disabled={uploadingAttachment}
            onChange={async (e) => {
                if (e.target.files && e.target.files.length > 0) {
                  try {
                    setUploadingAttachment(true);
                    const user = auth.currentUser;
                    if (!user) {
                      alert('You must be signed in to upload attachments');
                      return;
                    }

                    const files = Array.from(e.target.files);
                    const newAttachments: TaskAttachment[] = [];
                    
                    for (const file of files) {
                      const formData = new FormData();
                      formData.append('file', file);
                      formData.append('clientId', user.uid);
                      formData.append('type', 'documents');

                      const response = await fetch('https://helpkeepmymoney.com/upload.php', {
                        method: 'POST',
                        body: formData,
                      });

                      if (!response.ok) {
                        throw new Error(`Failed to upload file: ${file.name}`);
                      }

                      const result = await response.json();
                      
                      let userName = user.displayName || user.email || 'Unknown';
                      try {
                        const userDocRef = doc(db, 'users', user.uid);
                        const userDocSnap = await getDoc(userDocRef);
                        if (userDocSnap.exists()) {
                          const userData = userDocSnap.data();
                          userName = (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : userData.displayName) || userName;
                        }
                      } catch (err) {
                        // Fallback to displayName or email
                      }

                      newAttachments.push({
                        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
                        url: result.fileUrl,
                        fileName: result.originalName || file.name,
                        fileSize: result.size || file.size,
                        uploadedBy: user.uid,
                        uploadedByName: userName,
                        uploadedAt: new Date()
                      });
                    }

                    // Update task with new attachments
                    const taskRef = doc(db, 'tasks', currentTask.id);
                    const currentAttachments = currentTask.attachments || [];
                    const updatedAttachments = [...currentAttachments, ...newAttachments];
                    await updateDoc(taskRef, {
                      attachments: updatedAttachments,
                      updatedAt: new Date()
                    });

                    // Update local state immediately so UI updates without waiting for reload
                    setCurrentTask(prev => ({
                      ...prev,
                      attachments: updatedAttachments,
                      updatedAt: new Date()
                    }));
                    
                    // Also reload tasks to sync with the tasks list
                    onUpdate();
                    
                    e.target.value = ''; // Clear file input
                  } catch (error: any) {
                    alert(`Failed to upload attachments: ${error.message}`);
                  } finally {
                    setUploadingAttachment(false);
                  }
                }
              }}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '0.875rem'
              }}
            />
            <small style={{ display: 'block', marginTop: '0.25rem', color: '#6c757d', fontSize: '0.875rem' }}>
              {uploadingAttachment ? 'Uploading...' : 'Select files to attach (Max 10MB per file)'}
            </small>
          </div>
      </div>

      {/* Comments Section */}
      <TaskCommentsSection task={currentTask} onUpdate={onUpdate} role={role} />
    </div>
  );
};

interface TaskCommentsSectionProps {
  task: Task;
  onUpdate: () => void;
  role: 'admin' | 'staff' | 'client' | null;
}

const TaskCommentsSection: React.FC<TaskCommentsSectionProps> = ({ task, onUpdate, role }) => {
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState<TaskComment[]>(task.comments || []);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      const user = auth.currentUser;
      if (!user) {
        alert('You must be signed in to add comments');
        return;
      }

      // Get user name
      let userName = user.displayName || user.email || 'Unknown';
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          userName = (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : userData.displayName) || userName;
        }
      } catch (err) {
        // Fallback to displayName or email
      }

      const comment: TaskComment = {
        id: Date.now().toString(),
        userId: user.uid,
        userName,
        comment: newComment.trim(),
        createdAt: new Date()
      };

      const updatedComments = [...comments, comment];
      const taskRef = doc(db, 'tasks', task.id);
      await updateDoc(taskRef, {
        comments: updatedComments,
        updatedAt: new Date()
      });

      setComments(updatedComments);
      setNewComment('');
      onUpdate();
    } catch (error: any) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment: ' + error.message);
    }
  };

  return (
    <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #dee2e6' }}>
      <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#495057' }}>Comments & Notes</h3>
      
      <div style={{ marginBottom: '1rem', maxHeight: '300px', overflowY: 'auto' }}>
        {comments.length === 0 ? (
          <p style={{ color: '#6c757d', fontSize: '0.875rem' }}>No comments yet.</p>
        ) : (
          comments.map((comment, index) => (
            <div key={index} style={{
              marginBottom: '1rem',
              padding: '0.75rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <strong style={{ fontSize: '0.875rem' }}>{comment.userName || 'Unknown'}</strong>
                <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                  {(() => {
                    if (!comment.createdAt) return 'N/A';
                    // Handle Firestore Timestamp
                    if (comment.createdAt.toDate && typeof comment.createdAt.toDate === 'function') {
                      return comment.createdAt.toDate().toLocaleString();
                    }
                    // Handle JavaScript Date object
                    if (comment.createdAt instanceof Date) {
                      return comment.createdAt.toLocaleString();
                    }
                    // Handle date string or number (timestamp)
                    try {
                      const date = new Date(comment.createdAt);
                      if (!isNaN(date.getTime())) {
                        return date.toLocaleString();
                      }
                    } catch (e) {
                      // fall through to N/A
                    }
                    return 'N/A';
                  })()}
                </span>
              </div>
              <div style={{ fontSize: '0.875rem' }}>{comment.comment}</div>
            </div>
          ))
        )}
      </div>

      <div>
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a comment or note..."
          rows={3}
          style={{
            width: '100%',
            padding: '0.5rem',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            fontSize: '0.875rem',
            fontFamily: 'inherit',
            marginBottom: '0.5rem'
          }}
        />
        <button
          onClick={handleAddComment}
          disabled={!newComment.trim()}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: newComment.trim() ? '#007bff' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: newComment.trim() ? 'pointer' : 'not-allowed',
            fontSize: '0.875rem'
          }}
        >
          Add Comment
        </button>
      </div>
    </div>
  );
};

export default TaskManagement;

